<script lang="ts">
    import { createEventDispatcher, onMount } from "svelte";
    import { fade, scale } from "svelte/transition";
    import { elasticOut, backOut, cubicOut } from "svelte/easing";
    import CardBack from "./CardBack.svelte";
    import { CARD_BACKS, CARD_FACES, BOARD_COLORS } from "./lib/content";

    export let unlockedId: string;
    export let targetRect: DOMRect | null = null;
    export let dealSeed: string | null = null;
    export let isDuplicate = false;
    export let canReroll = true;

    const dispatch = createEventDispatcher();

    let isRevealed = false;
    let isOpening = false;
    let isClosing = false;
    let showCopyToast = false;

    function copyDealUrl() {
        if (!dealSeed) return;
        const url = new URL(window.location.href);
        url.searchParams.set("deal", dealSeed);
        navigator.clipboard.writeText(url.toString()).then(() => {
            showCopyToast = true;
            setTimeout(() => {
                showCopyToast = false;
            }, 2000);
        });
    }

    // Determine type and definition
    $: type = CARD_BACKS.some((b) => b.id === unlockedId)
        ? "back"
        : CARD_FACES.some((f) => f.id === unlockedId)
          ? "face"
          : BOARD_COLORS.some((b) => b.id === unlockedId)
            ? "board"
            : "unknown";

    $: def =
        type === "back"
            ? CARD_BACKS.find((b) => b.id === unlockedId)
            : type === "face"
              ? CARD_FACES.find((f) => f.id === unlockedId)
              : type === "board"
                ? BOARD_COLORS.find((b) => b.id === unlockedId)
                : null;

    function handleOpen() {
        if (isOpening || isRevealed) return;
        isOpening = true;

        // Animation sequence
        setTimeout(() => {
            isRevealed = true;
            isOpening = false;
        }, 800); // Slightly longer for the spin
    }

    function handleDismiss() {
        if (!isRevealed || isClosing) return;
        dispatch("dismiss");
    }

    let itemElement: HTMLElement;

    $: if (targetRect && itemElement && !isClosing) {
        isClosing = true;
        const startRect = itemElement.getBoundingClientRect();
        const deltaX =
            targetRect.left +
            targetRect.width / 2 -
            (startRect.left + startRect.width / 2);
        const deltaY =
            targetRect.top +
            targetRect.height / 2 -
            (startRect.top + startRect.height / 2);
        const scaleX = targetRect.width / startRect.width;
        const scaleY = targetRect.height / startRect.height;
        const scaleVal = Math.min(scaleX, scaleY);

        itemElement.style.transition =
            "transform 0.6s cubic-bezier(0.2, 0, 0.2, 1)";
        itemElement.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${scaleVal})`;

        // Fade out other elements
        const others = document.querySelectorAll(".fade-on-close");
        others.forEach((el) => ((el as HTMLElement).style.opacity = "0"));

        setTimeout(() => {
            dispatch("close");
        }, 600);
    }
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class="overlay" in:fade={{ duration: 400 }} on:click={handleDismiss}>
    <div class="content">
        {#if !isRevealed}
            <div
                class="mystery-pack"
                on:click|stopPropagation={handleOpen}
                class:opening={isOpening}
                class:idle={!isOpening}
            >
                <div class="pack-inner">
                    <span class="question-mark">?</span>
                </div>
            </div>
        {:else}
            <div
                class="reveal-container"
                in:scale={{ duration: 800, easing: elasticOut, start: 0.5 }}
            >
                {#if isDuplicate && canReroll}
                    <button
                        class="share-btn reroll-btn top-reroll"
                        on:click|stopPropagation={() => dispatch("reroll")}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            ><path d="M23 4v6h-6"></path><path
                                d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"
                            ></path></svg
                        >
                        Reroll Duplicate
                    </button>
                {/if}
                <div class="item-display-wrapper">
                    {#key unlockedId}
                        <div
                            class="item-display"
                            bind:this={itemElement}
                            class:spinning={!isClosing}
                        >
                            {#if type === "back"}
                                <div class="card-preview">
                                    <CardBack variant={unlockedId} />
                                </div>
                            {:else if type === "face"}
                                <div class="face-preview">
                                    <div class="face-name">{def?.name}</div>
                                    <div class="face-sub">Card Face Style</div>
                                </div>
                            {:else if type === "board"}
                                <div
                                    class="board-preview"
                                    style="background: {def?.value}"
                                ></div>
                            {/if}
                        </div>
                    {/key}
                </div>

                <div class="info-container fade-on-close">
                    <h2 class="item-name">{def?.name}</h2>
                    {#if def?.artist}
                        <div class="artist-info">
                            <span class="artist">{def.artist}</span>
                            {#if def?.year}
                                <span class="year">• {def.year}</span>
                            {/if}
                        </div>
                    {/if}
                    {#if dealSeed && type === "back"}
                        <button
                            class="share-btn"
                            on:click|stopPropagation={copyDealUrl}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                ><path
                                    d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"
                                ></path><polyline points="16 6 12 2 8 6"
                                ></polyline><line x1="12" y1="2" x2="12" y2="15"
                                ></line></svg
                            >
                            Share Deal
                        </button>
                    {/if}
                </div>
            </div>
        {/if}
    </div>
    {#if showCopyToast}
        <div class="toast" transition:fade>Deal link copied</div>
    {/if}
</div>

<style>
    .overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        /* Background handled by App.svelte shared-backdrop */
        background: transparent;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .content {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        pointer-events: none; /* Allow clicks on overlay */
    }

    .mystery-pack {
        pointer-events: auto;
        width: 200px;
        height: 280px;
        /* Elegant dark background */
        background: #2c3e50;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        /* Soft, realistic shadow */
        box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
        position: relative;
        overflow: hidden;
        transform-style: preserve-3d;
    }

    /* Removed inner border and sheen for a cleaner look */
    .mystery-pack::before,
    .mystery-pack::after {
        display: none;
    }

    .mystery-pack.idle {
        animation: float 4s ease-in-out infinite;
    }

    .mystery-pack.opening {
        animation: open-spin 1.2s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    .mystery-pack::before {
        /* Removed old radial gradient */
        display: none;
    }

    .pack-inner {
        position: relative;
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        color: inherit;
    }

    .question-mark {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            Helvetica, Arial, sans-serif;
        font-size: 100px;
        font-weight: 700;
        line-height: 1;
        color: #f4f1ea;
        opacity: 1;
        /* Removed gradient and shadow for flat design */
        background: none;
        background-clip: border-box;
        -webkit-background-clip: border-box;
        -webkit-text-fill-color: currentcolor;
        filter: none;
    }

    .reveal-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        pointer-events: auto;
    }

    .item-display-wrapper {
        perspective: 1000px;
        margin-bottom: 30px;
    }

    .item-display {
        filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.6));
        transform-origin: center center;
    }

    /* Initial spin on reveal */
    .item-display.spinning {
        animation: reveal-spin 1.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .card-preview {
        width: 280px; /* Larger size */
        height: 392px;
    }

    .face-preview {
        width: 280px;
        height: 392px;
        background: white;
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #333;
    }

    .face-name {
        font-size: 24px;
        font-weight: bold;
    }

    .board-preview {
        width: 280px;
        height: 280px;
        border-radius: 16px;
        border: 6px solid white;
    }

    .info-container {
        text-align: center;
        color: white;
        transition: opacity 0.3s;
    }

    .item-name {
        font-size: 36px;
        margin: 0 0 8px 0;
        font-weight: bold;
        text-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    }

    .artist-info {
        font-size: 18px;
        opacity: 0.9;
        margin-bottom: 24px;
        text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
    }

    .share-btn {
        background: rgba(255, 255, 255, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.4);
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 1rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 auto;
        transition: all 0.2s;
        pointer-events: auto;
    }

    .share-btn:hover {
        background: rgba(255, 255, 255, 0.3);
        transform: translateY(-2px);
    }

    .top-reroll {
        margin-bottom: 20px;
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.3);
    }

    .toast {
        position: absolute;
        bottom: 100px;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 10px 20px;
        border-radius: 25px;
        font-size: 1rem;
        pointer-events: none;
        z-index: 10001;
        white-space: nowrap;
    }

    @keyframes sheen {
        0% {
            left: -150%;
        }
        50% {
            left: 150%;
        }
        100% {
            left: 150%;
        }
    }

    @keyframes rotate {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    @keyframes float {
        0%,
        100% {
            transform: translateY(0) rotateX(0);
        }
        50% {
            transform: translateY(-8px) rotateX(5deg);
        }
    }

    @keyframes open-spin {
        0% {
            transform: scale(1) rotateY(0);
        }
        40% {
            transform: scale(0.8) rotateY(180deg);
        }
        100% {
            transform: scale(1.5) rotateY(360deg);
            opacity: 0;
        }
    }

    @keyframes reveal-spin {
        0% {
            transform: scale(0.5) rotateY(-180deg);
        }
        100% {
            transform: scale(1) rotateY(0);
        }
    }

    @keyframes pulse {
        0%,
        100% {
            opacity: 0.6;
        }
        50% {
            opacity: 1;
        }
    }

    @media (max-height: 700px) {
        .card-preview,
        .face-preview {
            width: 200px;
            height: 280px;
        }
        .board-preview {
            width: 200px;
            height: 200px;
        }
        .item-name {
            font-size: 28px;
        }
        .artist-info {
            font-size: 16px;
            margin-bottom: 16px;
        }
        .top-reroll {
            margin-bottom: 10px;
        }
    }

    @media (max-height: 500px) {
        .card-preview,
        .face-preview {
            width: 140px;
            height: 196px;
        }
        .board-preview {
            width: 140px;
            height: 140px;
        }
        .item-name {
            font-size: 24px;
            margin-bottom: 4px;
        }
        .artist-info {
            font-size: 14px;
            margin-bottom: 8px;
        }
        .share-btn {
            padding: 6px 12px;
            font-size: 0.9rem;
        }
    }
</style>
